import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, ErrorState, Field, Input } from '@bb/ui';
import type { AppInstanceSummary, AppPushCapabilities } from '../../../api/client.ts';
import { problemFieldErrors, problemText } from './problems.ts';
import { pushKeys, useApi } from './usePushSettingsData.ts';

/**
 * What each app declares about push (BB-77) until the SDKs report it: iOS time-sensitive entitlement and Notification
 * Service Extension (images), the Android small icon for the generated config. Changing it needs `apps.manage`.
 */
export function AppCapabilitiesSection({ environmentId, apps, canEdit }: { environmentId: string; apps: AppInstanceSummary[]; canEdit: boolean }) {
  if (apps.length === 0) return <EmptyState title="No Android or iOS apps" />;
  return (
    <div className="push-capabilities">
      {!canEdit && <p className="hint">Declared by people with apps.manage.</p>}
      {apps.map((app) => (
        <AppCapabilitiesForm key={app.id} environmentId={environmentId} app={app} canEdit={canEdit} />
      ))}
    </div>
  );
}

function AppCapabilitiesForm({ environmentId, app, canEdit }: { environmentId: string; app: AppInstanceSummary; canEdit: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = pushKeys.capabilitiesOf(environmentId, app.id);
  const saved = useQuery({ queryKey: key, queryFn: () => api.appPushCapabilities(environmentId, app.id) });
  const [draft, setDraft] = useState<AppPushCapabilities | null>(null);
  const save = useMutation({
    mutationFn: (body: AppPushCapabilities) => api.putAppPushCapabilities(environmentId, app.id, body),
    onSuccess: async (next) => {
      setDraft(null);
      queryClient.setQueryData(key, next);
      await queryClient.invalidateQueries({ queryKey: pushKeys.capabilities(environmentId) });
    },
  });
  if (saved.isError) return <ErrorState title={`Capabilities of ${app.name} could not be loaded`} message={problemText(saved.error)} onRetry={() => void saved.refetch()} />;
  if (!saved.data) return <p aria-busy="true">Loading {app.name}…</p>;
  const value = draft ?? saved.data;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved.data);
  const set = (patch: Partial<AppPushCapabilities>) => {
    save.reset();
    setDraft({ ...value, ...patch });
  };
  const icon = value.androidSmallIconRes ?? '';
  const server = problemFieldErrors(save.error);
  return (
    <fieldset className="push-app-capabilities">
      <legend>{app.name}</legend>
      {app.platform === 'ios' ? (
        <>
          <Checkbox label="Time-sensitive notifications" checked={value.iosTimeSensitiveEnabled} disabled={!canEdit} onCheckedChange={(iosTimeSensitiveEnabled) => set({ iosTimeSensitiveEnabled })} />
          <p className="hint">The app has the com.apple.developer.usernotifications.time-sensitive entitlement; without it time-sensitive pushes arrive as active.</p>
          <Checkbox label="Notification Service Extension" checked={value.iosNotificationServiceExtension} disabled={!canEdit} onCheckedChange={(iosNotificationServiceExtension) => set({ iosNotificationServiceExtension })} />
          <p className="hint">Needed for images in iOS pushes.</p>
        </>
      ) : (
        <Field label="Small icon resource" hint="Drawable name for the generated config, such as ic_push_notification." error={icon.length > 100 ? 'At most 100 characters.' : server.androidSmallIconRes}>
          <Input value={icon} spellCheck={false} disabled={!canEdit} onChange={(e) => set({ androidSmallIconRes: e.target.value.trim() ? e.target.value : null })} />
        </Field>
      )}
      {canEdit && (
        <div className="actions">
          <Button size="sm" loading={save.isPending} disabled={!dirty || icon.length > 100} onClick={() => save.mutate({ ...value, androidSmallIconRes: value.androidSmallIconRes?.trim() || null })}>
            Save
          </Button>
          {save.isSuccess && !draft && <span role="status">Saved.</span>}
        </div>
      )}
      {save.isError && Object.keys(server).length === 0 && <p role="alert">{problemText(save.error)}</p>}
    </fieldset>
  );
}
